import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { apiFetch } from '../lib/api'
import type {
  AccountType,
  TeamPermissions,
  UserProfile,
} from '../types/database'

// Response shape from GET /api/user/profile. Backend Phase 3 extended this
// with team-context fields when the authenticated user is a team_member.
type UserProfileResponse = UserProfile & {
  permissions?: TeamPermissions
  teamMemberId?: string
  adminDisplayName?: string | null
  adminEmail?: string | null
}

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: UserProfile | null
  // Team-context fields (undefined for podcasters/network_admin, present for team_member).
  accountType: AccountType | undefined
  permissions: TeamPermissions | undefined
  adminDisplayName: string | null | undefined
  adminEmail: string | null | undefined
  teamMemberId: string | undefined
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, displayName?: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const SESSION_KEY = 'grasppod-session-exists'
const SESSION_REFRESH_TIMEOUT = 4000

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [accountType, setAccountType] = useState<AccountType | undefined>(undefined)
  const [permissions, setPermissions] = useState<TeamPermissions | undefined>(undefined)
  const [adminDisplayName, setAdminDisplayName] = useState<string | null | undefined>(undefined)
  const [adminEmail, setAdminEmail] = useState<string | null | undefined>(undefined)
  const [teamMemberId, setTeamMemberId] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  // Fetch profile via /api/user/profile so backend service_role resolves
  // RLS-restricted admin context fields when the user is a team_member.
  const fetchProfile = useCallback(async () => {
    try {
      const data = await apiFetch<UserProfileResponse>('/user/profile')
      setProfile(data)
      setAccountType(data.account_type)
      setPermissions(data.permissions)
      setAdminDisplayName(data.adminDisplayName)
      setAdminEmail(data.adminEmail)
      setTeamMemberId(data.teamMemberId)
    } catch {
      // Surface via error page — profile stays null, gates will fall through.
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile()
  }, [user, fetchProfile])

  useEffect(() => {
    let mounted = true

    async function initAuth() {
      // Quick check: if no session existed before, skip the slow path
      const hadSession = localStorage.getItem(SESSION_KEY) === 'true'
      if (!hadSession) {
        setLoading(false)
        return
      }

      // Get existing session with timeout to prevent deadlocks
      const sessionPromise = supabase.auth.getSession()
      const timeoutPromise = new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), SESSION_REFRESH_TIMEOUT)
      )

      const result = await Promise.race([sessionPromise, timeoutPromise])

      if (!mounted) return

      if (result && 'data' in result && result.data.session) {
        const sess = result.data.session
        setSession(sess)
        setUser(sess.user)
        localStorage.setItem(SESSION_KEY, 'true')
        await fetchProfile()
      } else {
        localStorage.removeItem(SESSION_KEY)
      }

      setLoading(false)
    }

    initAuth()

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        if (!mounted) return

        setSession(newSession)
        setUser(newSession?.user ?? null)

        if (newSession?.user) {
          localStorage.setItem(SESSION_KEY, 'true')
          await fetchProfile()
        } else {
          localStorage.removeItem(SESSION_KEY)
          setProfile(null)
          setAccountType(undefined)
          setPermissions(undefined)
          setAdminDisplayName(undefined)
          setAdminEmail(undefined)
          setTeamMemberId(undefined)
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [fetchProfile])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  const signUp = useCallback(async (email: string, password: string, displayName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName || null },
      },
    })
    if (error) throw error
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    localStorage.removeItem(SESSION_KEY)
    setProfile(null)
    setAccountType(undefined)
    setPermissions(undefined)
    setAdminDisplayName(undefined)
    setAdminEmail(undefined)
    setTeamMemberId(undefined)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        accountType,
        permissions,
        adminDisplayName,
        adminEmail,
        teamMemberId,
        loading,
        signIn,
        signUp,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
